
/**
 * User roles available in the platform.
 */
export enum UserRole {
  PLATFORM_OWNER = 'super_admin',
  TENANT_ADMIN = 'tenant_admin',
  ANALYST = 'analyst',
  CLIENT_VIEWER = 'client_viewer',
}

/**
 * Supported filter types for metadata.
 */
export enum FilterType {
  DROPDOWN = 'dropdown',
  TEXT = 'text',
  DATE = 'date',
  NUMBER = 'number',
  MULTI = 'multi',
}

/**
 * Processing status of a video evaluation.
 */
export enum VideoStatus {
  PROCESSING = 'processing',
  READY = 'ready',
  ERROR = 'error',
}

/**
 * Represents a Tenant (Agency) in the multi-tenant system.
 */
export interface Tenant {
  tenantId: string;
  name: string;
  active: boolean;
  settings: {
    branding: {
      logoUrl: string;
      primaryColor: string;
    };
    maxVideos: number;
    maxUsers: number;
  };
  createdAt: any;
}

/**
 * Represents a Company (Client) belonging to a Tenant.
 */
export interface Company {
  companyId: string;
  tenantId: string;
  name: string;
  settings: {
    branding: {
      primaryColor: string;
      backgroundColor: string;
      logoUrl: string;
      bannerText: string;
    };
    defaultFilters: string[];
    timezone: string;
  };
  reportSettings?: {
    headerLogos?: {
      left?: string;
      right?: string;
    };
  };
  createdBy: string;
  createdAt: any;
}

/**
 * Represents a User in the system.
 */
export interface User {
  uid: string;
  email: string;
  name: string;
  role: UserRole;
  active: boolean;
  tenantId: string;
  companyId?: string;
  createdAt: any;
}

/**
 * Custom filter definition for a company's videos.
 */
export interface Filter {
  filterId: string;
  companyId: string;
  tenantId: string;
  name: string;
  type: FilterType;
  options?: string[];
  createdAt: any;
}

/**
 * Represents an uploaded video and its metadata.
 */
export interface Video {
  videoId: string;
  companyId: string;
  tenantId: string;
  title: string;
  description: string;
  storagePath: string;
  filters: Record<string, any>;
  status: VideoStatus;
  aiAnalysisId: string | null;
  uploadedBy: string;
  thumbnailUrl: string;
  createdAt: any;
}

/**
 * KPI result for a specific criterion.
 */
export interface Kpi {
  score: number;
  analysis: string;
}

/**
 * Map of KPI results by their ID.
 */
export type AiAnalysisKpis = Record<string, Kpi | string>;

/**
 * Version record for an AI analysis.
 */
export interface AnalysisVersion {
  v: number;
  editor: string;
  timestamp: any;
  kpis: AiAnalysisKpis;
}

/**
 * Full AI analysis record for a video.
 */
export interface AiAnalysis {
  analysisId: string;
  videoId: string;
  tenantId: string;
  companyId: string;
  kpis: AiAnalysisKpis;
  versions: AnalysisVersion[];
  generatedAt: any;
}

/**
 * Definition of a KPI to be evaluated by the AI.
 */
export interface KpiDefinition {
  id: string;
  title: string;
  description: string;
}
